import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { serviceCategories } from '../data/servicesContent';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Accueil' },
    { path: '/qui-suis-je', label: 'Qui suis-je ?' },
    {
      path: '/services',
      label: 'Services',
      children: serviceCategories.map((c) => ({ path: `/services#${c.id}`, label: c.label }))
    },
    { path: '/contact', label: 'Contact' }
  ];

  const isActive = (path) => location.pathname === path;

  // Referme le sous-menu (ouvert au survol / focus) après un clic.
  const closeDropdown = () => document.activeElement?.blur();

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 font-sans ${
        isScrolled ? 'bg-[#F5F3EB] shadow-md' : 'bg-[#F5F3EB]/85 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <img
              src="/logo_1-transparent.png"
              alt="Tamila Hachemi"
              className="h-[55px] w-auto object-contain group-hover:scale-105 transition-transform"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => {
              const linkElement = (
                <Link
                  to={link.path}
                  onClick={link.children ? closeDropdown : undefined}
                  aria-haspopup={link.children ? 'true' : undefined}
                  className={`relative inline-flex items-center text-base font-medium tracking-[0.02em] transition-colors duration-200 ${
                    isActive(link.path)
                      ? 'text-[#21167D]'
                      : 'text-[#4A4458] hover:text-[#21167D]'
                  }`}
                >
                  {link.label}
                  {link.children && (
                    <ChevronDown className="w-4 h-4 ml-1 transition-transform group-hover:rotate-180 group-focus-within:rotate-180" />
                  )}
                  {isActive(link.path) && (
                    <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#EAA93D] rounded-full"></span>
                  )}
                </Link>
              );

              if (!link.children) {
                return <React.Fragment key={link.path}>{linkElement}</React.Fragment>;
              }

              return (
                <div key={link.path} className="relative group">
                  {linkElement}
                  {/* Sous-menu déroulant (survol ou focus clavier) */}
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 invisible opacity-0 translate-y-1 transition-all duration-200 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 group-focus-within:visible group-focus-within:opacity-100 group-focus-within:translate-y-0">
                    <ul className="w-64 py-2 bg-[#FDFBF5] rounded-xl shadow-xl border border-[#DCB071]/40">
                      {link.children.map((child) => (
                        <li key={child.path}>
                          <Link
                            to={child.path}
                            onClick={closeDropdown}
                            className="block px-4 py-2.5 text-sm font-medium text-[#4A4458] hover:bg-[#DCB071]/15 hover:text-[#21167D] focus:bg-[#DCB071]/15 focus:text-[#21167D] focus:outline-none transition-colors"
                          >
                            {child.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>

          {/* CTA Button */}
          <div className="hidden md:block">
            <Link to="/rendez-vous">
              <Button className="shadow-md">
                <Phone className="w-4 h-4 mr-2" />
                Prendre RDV
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-[#DCB071]/20 transition-colors"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-[#21167D]" />
            ) : (
              <Menu className="w-6 h-6 text-[#21167D]" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden transition-all duration-300 ease-in-out overflow-hidden ${
          isMobileMenuOpen ? 'max-h-[calc(100vh-5rem)] overflow-y-auto opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-4 pt-2 pb-4 space-y-3 bg-[#F5F3EB] border-t border-[#DCB071]/40">
          {navLinks.map((link) => (
            <div key={link.path}>
              <Link
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-lg text-base font-medium transition-all ${
                  isActive(link.path)
                    ? 'bg-[#DCB071]/15 text-[#21167D]'
                    : 'text-[#4A4458] hover:bg-[#DCB071]/10'
                }`}
              >
                {link.label}
              </Link>
              {link.children && (
                <div className="mt-1 ml-4 pl-3 border-l-2 border-[#DCB071]/40 space-y-1">
                  {link.children.map((child) => (
                    <Link
                      key={child.path}
                      to={child.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block px-3 py-2 rounded-lg text-sm font-medium text-[#4A4458] hover:bg-[#DCB071]/10 hover:text-[#21167D] transition-all"
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          <Link to="/rendez-vous" onClick={() => setIsMobileMenuOpen(false)}>
            <Button className="w-full">
              <Phone className="w-4 h-4 mr-2" />
              Prendre RDV
            </Button>
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
